<template>
  <div class="calendar-page my-3">
    <div class="calendar-layout" :class="{ 'layout-list': viewMode === 'list' }">
      <div class="calendar-toolbar-card card shadow-sm border-0">
        <div class="card-body toolbar-card-body">
          <div class="toolbar-top">
            <div class="toolbar-brand">
              <span class="head-icon">
                <i class="bi bi-calendar-week"></i>
              </span>
              <div>
                <h4 class="toolbar-title mb-0">Calendar of Activities</h4>
                <p class="page-subtitle mb-0">Regional schedule — view-only</p>
              </div>
            </div>

            <div class="toolbar-stats">
              <span class="mini-stat">
                <strong>{{ shownCount }}</strong> shown
              </span>
              <span class="mini-stat mini-stat--week">
                <strong>{{ filteredSummaryEvents.length }}</strong>
                {{ summaryPeriodLabel.toLowerCase() }}
              </span>
              <span v-if="pendingReportCount" class="mini-stat mini-stat--danger">
                <i class="bi bi-exclamation-octagon-fill"></i>
                <strong>{{ pendingReportCount }}</strong>
                {{ pendingReportCount === 1 ? "missing report" : "missing reports" }}
              </span>
              <span v-if="readyForPostingCount" class="mini-stat mini-stat--posting">
                <i class="bi bi-megaphone-fill"></i>
                <strong>{{ readyForPostingCount }}</strong> for posting
              </span>
            </div>

            <div class="toolbar-top-actions">
              <div class="view-toggle" role="group" aria-label="View mode">
                <button
                  type="button"
                  class="view-toggle-btn"
                  :class="{ active: viewMode === 'calendar' }"
                  @click="setViewMode('calendar')"
                >
                  <i class="bi bi-calendar3"></i>
                  <span>Calendar</span>
                </button>
                <button
                  type="button"
                  class="view-toggle-btn"
                  :class="{ active: viewMode === 'list' }"
                  @click="setViewMode('list')"
                >
                  <i class="bi bi-list-ul"></i>
                  <span>List</span>
                </button>
              </div>
              <router-link to="/my-activities" class="btn btn-primary btn-sm">
                <i class="bi bi-calendar-check mr-1"></i> My Activities
              </router-link>
            </div>
          </div>

          <div class="toolbar-controls">
            <div class="toolbar-controls-group">
              <label class="toolbar-control">
                <span class="toolbar-control-label">Type</span>
                <select
                  id="calendar-type-filter"
                  v-model="filterType"
                  class="form-control form-control-sm"
                  @change="onFilterTypeChange"
                >
                  <option value="">All types</option>
                  <option v-for="type in eventTypes" :key="type" :value="type">{{ type }}</option>
                </select>
              </label>

              <div v-if="viewMode === 'list'" class="toolbar-control">
                <span id="calendar-period-label" class="toolbar-control-label">Period</span>
                <div class="view-toggle" role="group" aria-labelledby="calendar-period-label">
                  <button
                    v-for="option in listPeriodOptions"
                    :key="option.value"
                    type="button"
                    class="view-toggle-btn"
                    :class="{ active: listPeriod === option.value }"
                    :aria-pressed="listPeriod === option.value ? 'true' : 'false'"
                    @click="setListPeriod(option.value)"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>

              <button
                v-if="viewMode === 'calendar'"
                type="button"
                role="switch"
                class="layer-switch"
                :class="{ on: showLeaveLayer }"
                :aria-checked="showLeaveLayer ? 'true' : 'false'"
                @click="setLeaveLayer(!showLeaveLayer)"
              >
                <span class="layer-switch-track" aria-hidden="true">
                  <span class="layer-switch-thumb"></span>
                </span>
                Leave &amp; pass slips
              </button>
            </div>

            <div class="toolbar-controls-group">
              <button
                type="button"
                class="legend-toggle"
                :class="{ open: legendOpen }"
                :aria-expanded="legendOpen ? 'true' : 'false'"
                aria-controls="calendar-legend"
                @click="toggleLegend"
              >
                Legend
                <i class="bi legend-toggle-caret" :class="legendOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </button>

              <div class="toolbar-control">
                <label for="calendar-export-period" class="toolbar-control-label">Export</label>
                <select
                  id="calendar-export-period"
                  v-model="exportPeriod"
                  class="form-control form-control-sm"
                >
                  <option value="today">Today</option>
                  <option value="week">This week</option>
                  <option value="month">This month</option>
                </select>
                <button
                  type="button"
                  class="btn btn-sm btn-primary export-btn"
                  title="Download CSV"
                  aria-label="Download CSV"
                  :disabled="exporting"
                  @click="exportActivitiesCsv"
                >
                  <span v-if="exporting" class="spinner-border spinner-border-sm"></span>
                  <i v-else class="bi bi-download"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- One labelled row per group. Samples are built by the same code
               and CSS as the calendar blocks, so they always match. -->
          <div v-show="legendOpen" id="calendar-legend" class="legend-grid">
            <template v-for="group in legendGroups">
              <span :key="group.key + '-label'" class="legend-row-label">{{ group.label }}</span>
              <div :key="group.key + '-items'" class="legend-row">
                <span v-for="item in group.items" :key="item.key" class="legend-item">
                  <span
                    class="legend-chip"
                    :class="item.classNames"
                    :style="item.style"
                    v-html="item.html"
                  ></span>
                  {{ item.label }}
                </span>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Main content column -->
      <div class="calendar-main card shadow-sm border-0">
        <div
          class="card-body calendar-main-body"
          :class="{ 'calendar-main-body--grid': viewMode === 'calendar' }"
        >
          <!-- Calendar view -->
          <div v-show="viewMode === 'calendar'" ref="calendarShell" class="calendar-shell">
            <div v-if="eventsLoading" class="calendar-fetch-overlay">
              <span class="spinner-border spinner-border-sm text-primary"></span>
            </div>
            <div id="calendar-of-activities"></div>

            <div
              v-if="leavePopover"
              ref="leavePopover"
              class="leave-popover"
              role="dialog"
              aria-labelledby="leave-popover-name"
              :style="{
                top: leavePopover.top + 'px',
                left: leavePopover.left + 'px',
                '--evt-accent': leavePopover.accent,
              }"
            >
              <div class="leave-popover-head">
                <span
                  class="leave-popover-kind"
                  :style="{ backgroundColor: leavePopover.tint, color: leavePopover.textColor }"
                >
                  {{ leavePopover.kindLabel }}
                </span>
                <button
                  ref="leavePopoverClose"
                  type="button"
                  class="leave-popover-close"
                  aria-label="Close"
                  @click="closeLeavePopover(true)"
                >
                  <i class="bi bi-x"></i>
                </button>
              </div>
              <p id="leave-popover-name" class="leave-popover-name">{{ leavePopover.name }}</p>
              <p class="leave-popover-line">
                <i class="bi bi-calendar3"></i> {{ leavePopover.dateLabel }}
              </p>
              <p v-if="leavePopover.timeLabel" class="leave-popover-line">
                <i class="bi bi-clock"></i> {{ leavePopover.timeLabel }}
              </p>
            </div>
          </div>

          <!-- List view -->
          <div v-show="viewMode === 'list'" class="list-shell">
            <div class="list-toolbar">
              <div class="list-period-nav" role="group" aria-label="Change period">
                <button
                  type="button"
                  class="list-nav-btn"
                  :aria-label="'Previous ' + listPeriodNoun"
                  :title="'Previous ' + listPeriodNoun"
                  @click="shiftListPeriod(-1)"
                >
                  <i class="bi bi-chevron-left"></i>
                </button>
                <span class="list-period-label" aria-live="polite">{{ listRangeLabel }}</span>
                <button
                  type="button"
                  class="list-nav-btn"
                  :aria-label="'Next ' + listPeriodNoun"
                  :title="'Next ' + listPeriodNoun"
                  @click="shiftListPeriod(1)"
                >
                  <i class="bi bi-chevron-right"></i>
                </button>
                <button
                  v-if="!listIsCurrentPeriod"
                  type="button"
                  class="list-current-btn"
                  @click="resetListPeriod"
                >
                  {{ listCurrentLabel }}
                </button>
              </div>
              <div class="list-search-wrap">
                <i class="bi bi-search list-search-icon"></i>
                <input
                  v-model="listSearchQuery"
                  type="search"
                  class="form-control form-control-sm list-search-input"
                  placeholder="Search activities…"
                />
              </div>
              <span class="list-count">
                {{ filteredListEvents.length }} {{ filteredListEvents.length === 1 ? "result" : "results" }}
              </span>
            </div>

            <div v-if="listLoading" class="list-empty">
              <span class="spinner-border spinner-border-sm text-primary"></span>
              <p>Loading activities…</p>
            </div>
            <div v-else-if="!filteredListEvents.length" class="list-empty">
              <i class="bi bi-calendar2-x"></i>
              <p>No activities for {{ listRangeLabel }}</p>
            </div>
            <div v-else class="calendar-activity-list">
              <div
                v-for="item in filteredListEvents"
                :key="item.id"
                class="cal-list-row"
                :class="{ 'cal-list-row--report': item.needs_report }"
                role="button"
                tabindex="0"
                @click="openEventById(item.id)"
                @keydown.enter.prevent="openEventById(item.id)"
                @keydown.space.prevent="openEventById(item.id)"
              >
                <div class="cal-list-date">
                  <span class="cal-list-day">{{ formatWeekDay(item.start_date_time) }}</span>
                  <span class="cal-list-num">{{ formatWeekDate(item.start_date_time) }}</span>
                </div>
                <div class="cal-list-body">
                  <div class="cal-list-title-row">
                    <h6 class="cal-list-title">{{ item.title }}</h6>
                    <span v-if="item.is_private" class="cal-list-chip cal-list-chip--private">
                      <i class="bi bi-lock-fill"></i> Private
                    </span>
                    <span v-if="item.needs_report" class="cal-list-chip cal-list-chip--danger">
                      <i class="bi bi-exclamation-octagon-fill"></i> Missing report
                    </span>
                    <span v-else-if="isReadyForPosting(item)" class="cal-list-chip cal-list-chip--posting">
                      <i class="bi bi-megaphone-fill"></i> For posting
                    </span>
                  </div>
                  <p v-if="item.purpose" class="cal-list-purpose">{{ item.purpose }}</p>
                  <div class="cal-list-meta">
                    <span class="cal-list-meta-item">
                      <i class="bi bi-calendar3"></i>
                      {{ formatListDateRange(item) }}
                    </span>
                    <span v-if="item.location" class="cal-list-meta-item">
                      <i class="bi bi-geo-alt"></i> {{ item.location }}
                    </span>
                    <span v-if="modeMeta(item.activity_mode)" class="cal-list-meta-item">
                      <i class="bi" :class="modeMeta(item.activity_mode).icon"></i>
                      {{ modeMeta(item.activity_mode).label }}
                    </span>
                    <span
                      v-if="item.reference"
                      class="cal-list-meta-item"
                      title="Document tracking number"
                    >
                      <i class="bi bi-file-earmark-text"></i> DTN {{ item.reference }}
                    </span>
                    <span v-if="item.first_name || item.last_name" class="cal-list-meta-item">
                      <i class="bi bi-person"></i> {{ item.first_name }} {{ item.last_name }}
                    </span>
                  </div>
                </div>
                <div class="cal-list-aside">
                  <span
                    class="cal-list-type"
                    :style="{ backgroundColor: getEventBackgroundColor(item.type) }"
                  >
                    {{ getEventTypeLabel(item.type) }}
                  </span>
                  <span
                    v-if="!isDefaultDisplayStatus(item)"
                    class="cal-list-status"
                    :style="statusPillStyle(item)"
                  >
                    <i v-if="displayStatus(item).icon" class="bi" :class="displayStatus(item).icon"></i>
                    {{ displayStatus(item).label }}
                  </span>
                  <i class="bi bi-chevron-right cal-list-chevron"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Week sidebar (calendar view only) -->
      <div v-show="viewMode === 'calendar'" class="week-sidebar card shadow-sm border-0">
        <div class="card-body">
          <div v-if="myPendingReports.length" class="pending-reports-block mb-4">
            <div class="sidebar-header pending-reports-header">
              <div>
                <h6 class="sidebar-title mb-0 text-danger">
                  <i class="bi bi-exclamation-octagon-fill mr-1"></i> Missing reports
                </h6>
                <small class="sidebar-subtitle">Your activities need a report</small>
              </div>
              <span class="sidebar-count sidebar-count-danger">{{ myPendingReports.length }}</span>
            </div>
            <ul class="week-list mb-0">
              <li
                v-for="item in myPendingReports"
                :key="'report-' + item.id"
                class="week-item week-item-needs-report"
                role="button"
                tabindex="0"
                @click="goToSubmitReport(item.id)"
                @keydown.enter.prevent="goToSubmitReport(item.id)"
                @keydown.space.prevent="goToSubmitReport(item.id)"
              >
                <div class="week-date-block week-date-block-danger">
                  <span class="week-day">Due</span>
                  <span class="week-date">{{ formatWeekDate(item.end_date_time) }}</span>
                </div>
                <div class="week-content">
                  <p class="week-title">{{ item.title }}</p>
                  <span class="week-badge week-badge-report">
                    <i class="bi bi-journal-text"></i> Submit report
                  </span>
                </div>
                <i class="bi bi-chevron-right week-chevron"></i>
              </li>
            </ul>
          </div>

          <div v-if="myStatusUpdates.length" class="status-updates-block mb-4">
            <div class="sidebar-header status-updates-header">
              <div>
                <h6 class="sidebar-title mb-0 status-updates-title">
                  <i class="bi bi-arrow-repeat mr-1"></i> Update status
                </h6>
                <small class="sidebar-subtitle">
                  Ended in the last {{ statusUpdateDays }} days, still marked Scheduled
                </small>
              </div>
              <span class="sidebar-count sidebar-count-info">{{ myStatusUpdateTotal }}</span>
            </div>
            <ul class="week-list mb-0">
              <li
                v-for="item in myStatusUpdates"
                :key="'status-' + item.id"
                class="week-item week-item-status-update"
                role="button"
                tabindex="0"
                @click="goToUpdateStatus(item.id)"
                @keydown.enter.prevent="goToUpdateStatus(item.id)"
                @keydown.space.prevent="goToUpdateStatus(item.id)"
              >
                <div class="week-date-block">
                  <span class="week-day">Ended</span>
                  <span class="week-date">{{ formatWeekDate(item.end_date_time) }}</span>
                </div>
                <div class="week-content">
                  <p class="week-title">{{ item.title }}</p>
                  <span class="week-badge week-badge-update">
                    <i class="bi bi-arrow-repeat"></i> Update status
                  </span>
                </div>
                <i class="bi bi-chevron-right week-chevron"></i>
              </li>
            </ul>
            <router-link
              v-if="myStatusUpdateTotal > myStatusUpdates.length"
              :to="{ name: 'MyActivities' }"
              class="status-updates-more"
            >
              {{ myStatusUpdateTotal - myStatusUpdates.length }} more in My Activities
            </router-link>
          </div>

          <div class="sidebar-header">
            <div>
              <h6 class="sidebar-title mb-0">Activities</h6>
              <small class="sidebar-subtitle">{{ summaryRangeLabel }}</small>
            </div>
            <div class="sidebar-header-actions">
              <button
                type="button"
                class="btn btn-sm btn-light sidebar-copy-btn"
                :disabled="!filteredSummaryEvents.length || summaryLoading"
                title="Copy activities list"
                @click="copySummaryActivities"
              >
                <i class="bi bi-clipboard"></i>
              </button>
              <span class="sidebar-count">{{ filteredSummaryEvents.length }}</span>
            </div>
          </div>

          <div class="summary-period-toggle" role="group" aria-label="Summary period">
            <button
              type="button"
              class="summary-period-btn"
              :class="{ active: summaryPeriod === 'today' }"
              @click="setSummaryPeriod('today')"
            >
              Today
            </button>
            <button
              type="button"
              class="summary-period-btn"
              :class="{ active: summaryPeriod === 'week' }"
              @click="setSummaryPeriod('week')"
            >
              This week
            </button>
            <button
              type="button"
              class="summary-period-btn"
              :class="{ active: summaryPeriod === 'month' }"
              @click="setSummaryPeriod('month')"
            >
              This month
            </button>
          </div>

          <div v-if="summaryLoading" class="sidebar-empty">
            <span class="spinner-border spinner-border-sm"></span>
          </div>
          <div v-else-if="!filteredSummaryEvents.length" class="sidebar-empty">
            <i class="bi bi-calendar2-week"></i>
            <p>No activities for this period</p>
          </div>
          <ul v-else class="week-list mb-0">
            <li
              v-for="item in filteredSummaryEvents"
              :key="item.id"
              class="week-item"
              :class="{ 'week-item-needs-report': item.needs_report }"
              role="button"
              tabindex="0"
              @click="openEventById(item.id)"
              @keydown.enter.prevent="openEventById(item.id)"
              @keydown.space.prevent="openEventById(item.id)"
            >
              <div class="week-date-block">
                <span class="week-day">{{ formatWeekDay(item.start_date_time) }}</span>
                <span class="week-date">{{ formatWeekDate(item.start_date_time) }}</span>
              </div>
              <div class="week-content">
                <p class="week-title">{{ item.title }}</p>
                <div class="week-meta">
                  <span v-if="item.start_time" class="week-meta-item">
                    <i class="bi bi-clock"></i> {{ formatListTime(item.start_time, item.end_time) }}
                  </span>
                  <span v-if="item.location" class="week-meta-item">
                    <i class="bi bi-geo-alt"></i> {{ item.location }}
                  </span>
                  <span v-if="modeMeta(item.activity_mode)" class="week-meta-item">
                    <i class="bi" :class="modeMeta(item.activity_mode).icon"></i>
                    {{ modeMeta(item.activity_mode).label }}
                  </span>
                  <span
                    class="week-type-pill"
                    :style="{ backgroundColor: getEventBackgroundColor(item.type) }"
                  >
                    {{ getEventTypeLabel(item.type) }}
                  </span>
                </div>
                <div
                  v-if="item.needs_report || isReadyForPosting(item) || item.is_private || !isDefaultDisplayStatus(item)"
                  class="week-badges"
                >
                  <span v-if="item.needs_report" class="week-badge week-badge-report">
                    <i class="bi bi-exclamation-octagon-fill"></i> Missing report
                  </span>
                  <span v-if="isReadyForPosting(item)" class="week-badge week-badge-posting">
                    <i class="bi bi-megaphone-fill"></i> For posting
                  </span>
                  <span
                    v-if="item.is_private"
                    class="week-badge week-badge-private"
                  >
                    <i class="bi bi-lock-fill"></i> Private
                  </span>
                  <span
                    v-if="!isDefaultDisplayStatus(item)"
                    class="week-badge week-badge-status"
                    :style="statusPillStyle(item)"
                  >
                    <i v-if="displayStatus(item).icon" class="bi" :class="displayStatus(item).icon"></i>
                    {{ displayStatus(item).label }}
                  </span>
                </div>
              </div>
              <i class="bi bi-chevron-right week-chevron"></i>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <ViewEventModal
      ref="ViewEventModal"
      :read-only="true"
      @open-article-editor="openArticleEditor"
      @closed="clearEventQuery"
    />
    <ActivityReportModal ref="ActivityReportModal" />
  </div>
</template>

<script>
import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import "@fullcalendar/common/main.css";
import "@fullcalendar/daygrid/main.css";
import {
  EVENT_TYPES,
  NEEDS_REPORT_COLORS,
  LEAVE_LAYER_COLORS,
  getEventColors,
  getEventBackgroundColor,
  getEventTypeLabel,
  matchesEventType,
} from "../js/event-colors";
import {
  ACTIVITY_STATUSES,
  getActivityDisplayStatus,
  getStatusPillStyle,
  isDefaultDisplayStatus,
} from "../js/activity-status";
import {
  filterEventsByPeriod,
  eventsToExportRows,
  downloadCsv,
  getPeriodRange,
  formatPeriodRangeLabel,
  eventsToClipboardText,
  copyTextToClipboard,
  formatLocalDate,
} from "../js/activity-export";
import Papa from "papaparse";
import ViewEventModal from "./Calendar/components/ViewEventModal.vue";
import ActivityReportModal from "./Calendar/components/ActivityReportModal.vue";
import { isActivityLocked, hasSavedArticle, isReadyForPosting } from "../js/activity-lock";

const ACTIVITY_MODES = {
  "face-to-face": { icon: "bi-people-fill", label: "Face-to-face" },
  online: { icon: "bi-camera-video-fill", label: "Online" },
  hybrid: { icon: "bi-broadcast", label: "Hybrid" },
};

// Statuses that get their icon at the end of a calendar block.
const FLAGGED_STATUSES = ["completed", "postponed", "cancelled"];

const LIST_PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];
const LIST_PERIOD_NOUNS = { today: "day", week: "week", month: "month" };
const LIST_CURRENT_LABELS = { today: "Today", week: "This week", month: "This month" };

export default {
  name: "Calendar",
  components: {
    ViewEventModal,
    ActivityReportModal,
  },
  data() {
    return {
      calendar: null,
      eventsLoading: false,
      calendarReady: false,
      rawEvents: [],
      leaveEvents: [],
      summaryEvents: [],
      summaryPeriod: "week",
      summaryLoading: false,
      summaryRequestToken: 0,
      myPendingReports: [],
      pendingReportsLoading: false,
      filterType: "",
      exportPeriod: "week",
      exporting: false,
      eventTypes: EVENT_TYPES,
      pendingReportTotal: 0,
      eventsRequestToken: 0,
      fetchEventsTimer: null,
      visibleRange: null,
      viewMode: "calendar",
      listPeriod: "month",
      listPeriodOptions: LIST_PERIOD_OPTIONS,
      listEvents: [],
      listLoading: false,
      listRequestToken: 0,
      listSearchQuery: "",
      // Reference date for the list view's period; the arrows move it.
      listAnchor: new Date(),
      legendOpen: true,
      showLeaveLayer: true,
      leaveRequestToken: 0,
      leavePopover: null,
      myStatusUpdates: [],
      myStatusUpdateTotal: 0,
      statusUpdateDays: 30,
    };
  },
  computed: {
    // Legend rows. Type swatches come straight from the palette that colors
    // the blocks; every other sample is a miniature event built by the same
    // eventAppearance() + renderEventContent() the calendar uses, and styled
    // by the same CSS rules, so the legend always matches the grid.
    legendGroups() {
      const sample = (key, label, event) => {
        const appearance = this.eventAppearance({ type: "Organizer", ...event });
        return {
          key,
          label,
          classNames: appearance.classNames,
          style: { backgroundColor: appearance.backgroundColor, color: appearance.textColor },
          html: this.renderSampleContent(appearance.extendedProps),
        };
      };

      const groups = [
        {
          key: "type",
          label: "Type",
          items: EVENT_TYPES.map((type) => ({
            key: type,
            label: type,
            classNames: [],
            style: { backgroundColor: getEventBackgroundColor(type) },
            html: "",
          })),
        },
        {
          key: "status",
          label: "Status",
          items: [
            sample("report", "Missing report", { needs_report: true }),
            sample("posting", "For posting", { ready_for_posting: true }),
            sample("tentative", "Tentative", { is_tentative: true }),
            sample("private", "Private", { is_private: true }),
            ...FLAGGED_STATUSES.map((status) =>
              sample(status, ACTIVITY_STATUSES[status].label, { status })
            ),
          ],
        },
        {
          key: "mode",
          label: "Mode",
          items: Object.keys(ACTIVITY_MODES).map((mode) =>
            sample(mode, ACTIVITY_MODES[mode].label, { activity_mode: mode })
          ),
        },
      ];

      // Leave and pass slips only appear on the calendar grid.
      if (this.showLeaveLayer && this.viewMode === "calendar") {
        groups.push({
          key: "leave",
          label: "Leave",
          items: [
            { key: "leave", label: "On leave", colors: LEAVE_LAYER_COLORS.leave },
            { key: "pass-slip", label: "Pass slip", colors: LEAVE_LAYER_COLORS.pass_slip },
          ].map(({ key, label, colors }) => ({
            key,
            label,
            classNames: ["fc-leave-pass-slip-event"],
            style: { backgroundColor: colors.backgroundColor, color: colors.textColor },
            html: this.renderSampleContent({ accent: colors.borderColor }),
          })),
        });
      }

      return groups;
    },
    // The list shows its own period; the calendar shows the visible month.
    shownCount() {
      return this.viewMode === "list" ? this.filteredListEvents.length : this.displayEvents.length;
    },
    displayEvents() {
      return this.applyTypeFilter(this.rawEvents);
    },
    filteredSummaryEvents() {
      return this.applyTypeFilter(this.summaryEvents);
    },
    filteredListEvents() {
      let events = this.applyTypeFilter(this.listEvents);
      const query = String(this.listSearchQuery || "").trim().toLowerCase();
      if (query) {
        events = events.filter((event) => {
          const haystack = [
            event.title,
            event.purpose,
            event.location,
            event.reference,
            event.first_name,
            event.last_name,
            event.type,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(query);
        });
      }
      return events;
    },
    listRangeLabel() {
      return formatPeriodRangeLabel(this.listPeriod, this.listAnchor);
    },
    listIsCurrentPeriod() {
      const current = getPeriodRange(this.listPeriod).start.getTime();
      return getPeriodRange(this.listPeriod, this.listAnchor).start.getTime() === current;
    },
    listPeriodNoun() {
      return LIST_PERIOD_NOUNS[this.listPeriod];
    },
    listCurrentLabel() {
      return LIST_CURRENT_LABELS[this.listPeriod];
    },
    calendarEvents() {
      const leave = this.showLeaveLayer ? this.leaveEvents : [];
      return [
        ...this.displayEvents.map((event) => this.toCalendarEvent(event)),
        ...leave.map((row) => this.toLeaveCalendarEvent(row)),
      ];
    },
    pendingReportCount() {
      return this.pendingReportTotal;
    },
    readyForPostingCount() {
      return this.displayEvents.filter((e) => isReadyForPosting(e)).length;
    },
    summaryRangeLabel() {
      return formatPeriodRangeLabel(this.summaryPeriod);
    },
    summaryPeriodLabel() {
      if (this.summaryPeriod === "today") return "Today";
      if (this.summaryPeriod === "month") return "This month";
      return "This week";
    },
  },
  methods: {
    getEventBackgroundColor,
    getEventTypeLabel,
    isReadyForPosting,
    modeMeta(mode) {
      return ACTIVITY_MODES[mode] || null;
    },
    applyTypeFilter(events) {
      if (!this.filterType) return events;
      return events.filter((event) => matchesEventType(event.type, this.filterType));
    },
    compareByStartThenTitle(a, b) {
      const dateCompare = String(a.start_date_time || "").localeCompare(
        String(b.start_date_time || "")
      );
      if (dateCompare !== 0) return dateCompare;
      return String(a.title || "").localeCompare(String(b.title || ""));
    },
    onFilterTypeChange() {
      this.refreshCalendarEvents();
      this.getPendingReportsCount();
    },
    isDefaultDisplayStatus,
    displayStatus(activity) {
      return getActivityDisplayStatus(activity);
    },
    statusPillStyle(activity) {
      return getStatusPillStyle(getActivityDisplayStatus(activity));
    },
    readPreference(key) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    savePreference(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        /* ignore */
      }
    },
    isSmallScreen() {
      return !!(window.matchMedia && window.matchMedia("(max-width: 767.98px)").matches);
    },
    toggleLegend() {
      this.legendOpen = !this.legendOpen;
      this.savePreference("calendar-legend-open", this.legendOpen ? "1" : "0");
    },
    setLeaveLayer(show) {
      this.showLeaveLayer = show;
      this.savePreference("calendar-show-leave", show ? "1" : "0");
      if (!show) {
        this.closeLeavePopover(false);
        this.leaveEvents = [];
        this.refreshCalendarEvents();
      } else if (this.visibleRange) {
        this.getLeaveEvents(this.visibleRange.from, this.visibleRange.to);
      }
    },
    setViewMode(mode) {
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      this.savePreference("calendar-view-mode", mode);
      this.closeLeavePopover(false);
      if (mode === "list") {
        this.loadListEvents();
      } else {
        this.$nextTick(() => {
          if (this.calendar) this.calendar.updateSize();
        });
      }
    },
    setListPeriod(period) {
      if (this.listPeriod === period) return;
      this.listPeriod = period;
      this.loadListEvents();
    },
    shiftListPeriod(step) {
      const next = new Date(this.listAnchor);
      if (this.listPeriod === "month") {
        next.setDate(1); // avoid Jan 31 + 1 month rolling over into March
        next.setMonth(next.getMonth() + step);
      } else {
        next.setDate(next.getDate() + step * (this.listPeriod === "week" ? 7 : 1));
      }
      this.listAnchor = next;
      this.loadListEvents();
    },
    resetListPeriod() {
      this.listAnchor = new Date();
      this.loadListEvents();
    },
    formatListDateRange(activity) {
      const opts = { month: "short", day: "numeric", year: "numeric" };
      if (!activity.start_date_time) return "";
      const start = new Date(activity.start_date_time).toLocaleDateString("en-US", opts);
      if (!activity.end_date_time || activity.end_date_time === activity.start_date_time) {
        const timePart = activity.start_time
          ? ` · ${this.formatListTime(activity.start_time, activity.end_time)}`
          : "";
        return start + timePart;
      }
      const end = new Date(activity.end_date_time).toLocaleDateString("en-US", opts);
      // Multi-day activity: show the specific end time too, so an activity
      // that wraps up at a specific time (e.g. a half day) on the last day
      // doesn't read as if it runs the full last day.
      const startTimeText = this.formatListTime(activity.start_time, "");
      const endTimeText = this.formatListTime(activity.end_time, "");
      const startPart = startTimeText ? `${start}, ${startTimeText}` : start;
      const endPart = endTimeText ? `${end}, ${endTimeText}` : end;
      return `${startPart} – ${endPart}`;
    },
    formatListTime(startTime, endTime) {
      const fmt = (t) => {
        if (!t) return "";
        const [h, m] = String(t).split(":");
        const d = new Date();
        d.setHours(Number(h), Number(m), 0, 0);
        return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
      };
      const start = fmt(startTime);
      const end = fmt(endTime);
      if (start && end) return `${start} – ${end}`;
      return start || end;
    },
    // Compact start time for calendar blocks: "9am", "1:30pm".
    formatBlockTime(time) {
      const [h, m] = String(time || "").split(":").map(Number);
      if (!Number.isFinite(h)) return "";
      const hour = h % 12 || 12;
      const minutes = m ? `:${String(m).padStart(2, "0")}` : "";
      return `${hour}${minutes}${h < 12 ? "am" : "pm"}`;
    },
    formatWeekDay(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("en-US", { weekday: "short" });
    },
    formatWeekDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    },
    // Colors, CSS classes and marker flags for an activity block. Used for both
    // the calendar and the legend samples.
    eventAppearance(event) {
      const status = event.status || "scheduled";
      const needsReport = !!event.needs_report;
      const readyForPosting = !needsReport && !!event.ready_for_posting;
      // Same rule as the sidebar/list: "Tentative" only while still scheduled.
      const tentative = !!event.is_tentative && status === "scheduled";
      const colors = needsReport ? NEEDS_REPORT_COLORS : getEventColors(event.type);

      const classNames = [`fc-status-${status}`];
      if (needsReport) classNames.push("fc-needs-report");
      if (readyForPosting) classNames.push("fc-ready-for-posting");
      if (tentative) classNames.push("fc-tentative");
      if (event.is_private) classNames.push("fc-private");

      return {
        backgroundColor: colors.backgroundColor,
        borderColor: colors.borderColor,
        textColor: colors.textColor,
        classNames,
        extendedProps: {
          status,
          needs_report: needsReport,
          ready_for_posting: readyForPosting,
          activity_mode: event.activity_mode || null,
          is_tentative: tentative,
          is_private: !!event.is_private,
        },
      };
    },
    toCalendarEvent(event) {
      const appearance = this.eventAppearance({
        ...event,
        ready_for_posting: isReadyForPosting(event),
      });

      // Raw API fields are deliberately not spread in: FullCalendar merges
      // unknown top-level props into extendedProps, which would overwrite the
      // computed flags above (e.g. is_tentative on a completed activity).
      return {
        id: String(event.id),
        title: event.title,
        start: event.start_date_time,
        end: this.exclusiveEndDate(event.end_date_time || event.start_date_time),
        allDay: true,
        backgroundColor: appearance.backgroundColor,
        borderColor: appearance.borderColor,
        textColor: appearance.textColor,
        classNames: appearance.classNames,
        extendedProps: {
          ...appearance.extendedProps,
          display_title: event.title,
          start_time: event.start_time || null,
        },
      };
    },
    // Approved leave/pass-slip requests, shown as a distinct event layer
    // alongside regional activities (tinted chips with dark text, so
    // "someone's on leave" never reads as a solid-colored activity).
    toLeaveCalendarEvent(row) {
      const name = row.requester
        ? `${row.requester.last_name}, ${row.requester.first_name}`
        : row.requested_by;
      const typeLabel = row.type === "leave" ? (row.leave_category || "Leave") : "Pass Slip";
      const colors = LEAVE_LAYER_COLORS[row.type === "leave" ? "leave" : "pass_slip"];

      const formattedEnd = this.exclusiveEndDate(row.date_to || row.date_from);

      return {
        id: `lps-${row.id}`,
        title: `${name} — ${typeLabel}`,
        start: row.date_from,
        end: formattedEnd,
        backgroundColor: colors.backgroundColor,
        borderColor: colors.borderColor,
        textColor: colors.textColor,
        allDay: true,
        classNames: ["fc-leave-pass-slip-event"],
        extendedProps: {
          isLeaveEvent: true,
          leaveTypeLabel: typeLabel,
          display_title: `${name} — ${typeLabel}`,
          accent: colors.borderColor,
          // Only who / what / when is shown in the popup.
          leaveDetails: {
            name: row.requester
              ? `${row.requester.first_name} ${row.requester.last_name}`
              : row.requested_by,
            kindLabel: typeLabel,
            dateLabel: this.formatLeaveDates(row.date_from, row.date_to),
            timeLabel: row.type === "leave" ? "" : this.formatPassSlipTime(row),
            accent: colors.borderColor,
            tint: colors.backgroundColor,
            textColor: colors.textColor,
          },
        },
      };
    },
    formatLeaveDates(from, to) {
      // Date-only strings, read as local dates (new Date("YYYY-MM-DD") is UTC).
      const toDate = (value) => {
        const [y, m, d] = String(value || "").slice(0, 10).split("-").map(Number);
        return new Date(y, m - 1, d);
      };
      const opts = { weekday: "short", month: "short", day: "numeric" };
      const start = toDate(from);
      const end = toDate(to || from);
      const year = end.getFullYear();
      if (start.getTime() === end.getTime()) {
        return `${start.toLocaleDateString("en-US", opts)}, ${year}`;
      }
      return `${start.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", opts)}, ${year}`;
    },
    formatPassSlipTime(row) {
      if (!row.time_out) return "";
      const out = `Out ${this.formatListTime(row.time_out, "")}`;
      if (row.do_not_return) return `${out} · not returning`;
      return row.time_in ? `${out} · back ${this.formatListTime(row.time_in, "")}` : out;
    },
    dayHasPendingReport(date) {
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      return this.displayEvents.some((event) => {
        if (!event.needs_report) return false;
        const eventStart = new Date(event.start_date_time);
        eventStart.setHours(0, 0, 0, 0);
        const eventEnd = new Date(event.end_date_time || event.start_date_time);
        eventEnd.setHours(23, 59, 59, 999);
        return eventStart <= dayEnd && eventEnd >= dayStart;
      });
    },
    renderEventContent(arg) {
      const props = arg.event.extendedProps || {};
      const title = this.escapeHtml(
        props.display_title || String(arg.event.title || "")
      );
      const flag = (icon, modifier, label) =>
        `<i class="bi ${icon} fc-evt-flag fc-evt-flag--${modifier}" title="${label}"></i>`;
      const lead = [];
      const trail = [];

      if (props.needs_report) {
        lead.push('<span class="fc-evt-flag fc-evt-flag--report" title="Missing report">!</span>');
      } else if (props.ready_for_posting) {
        lead.push(flag("bi-megaphone-fill", "post", "For posting"));
      }
      const mode = ACTIVITY_MODES[props.activity_mode];
      if (mode) lead.push(flag(mode.icon, "mode", mode.label));

      if (props.is_tentative) {
        trail.push(flag(ACTIVITY_STATUSES.tentative.icon, "tent", ACTIVITY_STATUSES.tentative.label));
      }
      if (props.is_private) trail.push(flag("bi-lock-fill", "private", "Private"));
      if (FLAGGED_STATUSES.includes(props.status)) {
        const meta = ACTIVITY_STATUSES[props.status];
        trail.push(flag(meta.icon, "status", meta.label));
      }

      const time = props.start_time && arg.isStart !== false ? this.formatBlockTime(props.start_time) : "";
      const timeHtml = time ? `<span class="fc-evt-time">${time}</span>` : "";
      const titleHtml = title ? `<span class="fc-evt-title">${title}</span>` : "";
      const accentStyle = props.accent ? ` style="--evt-accent: ${props.accent}"` : "";
      return {
        html: `<div class="fc-evt-body"${accentStyle}>${[...lead, timeHtml, titleHtml, ...trail].join("")}</div>`,
      };
    },
    renderSampleContent(extendedProps) {
      // Legend labels sit right next to the samples, so drop the hover tooltips.
      return this.renderEventContent({ event: { title: "", extendedProps } }).html.replace(
        / title="[^"]*"/g,
        ""
      );
    },
    escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    initCalendar() {
      const calendarEl = document.getElementById("calendar-of-activities");
      if (!calendarEl) return;

      if (this.calendar) {
        this.calendar.destroy();
      }

      this.calendar = new Calendar(calendarEl, {
        plugins: [dayGridPlugin],
        initialView: "dayGridMonth",
        height: "auto",
        fixedWeekCount: false,
        headerToolbar: {
          left: "prev,next today",
          center: "title",
          right: "",
        },
        events: this.calendarEvents,
        eventClick: this.handleEventClick,
        eventContent: this.renderEventContent,
        eventOrder: this.compareCalendarBlocks,
        dayMaxEventRows: 4,
        moreLinkClick: "popover",
        moreLinkText: (n) => `+${n} more`,
        dayPopoverFormat: { weekday: "short", month: "short", day: "numeric" },
        dayCellDidMount: (info) => {
          if (this.dayHasPendingReport(info.date)) {
            info.el.classList.add("fc-day-has-pending-report");
          }
          if (info.date.getDay() === 0 || info.date.getDay() === 6) {
            info.el.classList.add("fc-day-weekend");
          }
        },
        datesSet: (info) => {
          this.closeLeavePopover(false);
          this.scheduleEventsFetch(info.start, info.end);
        },
      });

      this.calendar.render();
      this.calendarReady = true;
      this.$nextTick(() => {
        if (this.calendar) this.calendar.updateSize();
      });
    },
    // Missing-report and for-posting blocks first, then by start date,
    // activities before leave/pass slips, then by start time.
    compareCalendarBlocks(a, b) {
      const flag = (item, key) => (item.extendedProps[key] ? 1 : 0);
      return (
        flag(b, "needs_report") - flag(a, "needs_report") ||
        flag(b, "ready_for_posting") - flag(a, "ready_for_posting") ||
        a.start - b.start ||
        flag(a, "isLeaveEvent") - flag(b, "isLeaveEvent") ||
        String(a.extendedProps.start_time || "").localeCompare(
          String(b.extendedProps.start_time || "")
        ) ||
        String(a.title || "").localeCompare(String(b.title || ""))
      );
    },
    // FullCalendar treats an all-day event's end as exclusive, so it needs the
    // day after the last day. Built from local date parts: going through
    // toISOString() shifts the date back a day in timezones ahead of UTC.
    exclusiveEndDate(value) {
      const [y, m, d] = String(value || "").slice(0, 10).split("-").map(Number);
      return this.formatRangeDate(new Date(y, m - 1, d + 1));
    },
    formatRangeDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    scheduleEventsFetch(startDate, endDate) {
      const from = this.formatRangeDate(startDate);
      const to = this.formatRangeDate(endDate);
      this.visibleRange = { from, to };

      if (this.fetchEventsTimer) {
        clearTimeout(this.fetchEventsTimer);
      }

      this.fetchEventsTimer = setTimeout(() => {
        this.getEvents(from, to);
        if (this.showLeaveLayer) this.getLeaveEvents(from, to);
      }, 200);
    },
    getLeaveEvents(from, to) {
      const requestToken = ++this.leaveRequestToken;
      axios
        .get("api/leave-pass-slips/approved-in-range", { params: { from, to } })
        .then((response) => {
          if (requestToken !== this.leaveRequestToken || !this.showLeaveLayer) return;
          this.leaveEvents = response.data || [];
          this.refreshCalendarEvents();
        })
        .catch(() => {
          if (requestToken !== this.leaveRequestToken) return;
          this.leaveEvents = [];
        });
    },
    refreshCalendarEvents() {
      if (!this.calendar) return;
      this.calendar.removeAllEvents();
      this.calendarEvents.forEach((event) => {
        this.calendar.addEvent(event);
      });
      this.$nextTick(() => {
        this.refreshDayCellMarkers();
        if (this.calendar) this.calendar.updateSize();
      });
    },
    refreshDayCellMarkers() {
      if (!this.calendar) return;
      const dayCells = document.querySelectorAll("#calendar-of-activities .fc-daygrid-day");
      dayCells.forEach((el) => {
        el.classList.remove("fc-day-has-pending-report");
        const dateAttr = el.getAttribute("data-date");
        if (dateAttr && this.dayHasPendingReport(new Date(`${dateAttr}T00:00:00`))) {
          el.classList.add("fc-day-has-pending-report");
        }
      });
    },
    openArticleEditor(activity) {
      if (!this.$refs.ActivityReportModal) return;
      const payload = { ...activity };
      delete payload.readOnly;
      if ((activity.readOnly || isActivityLocked(activity)) && hasSavedArticle(activity)) {
        this.$refs.ActivityReportModal.openArticleView(payload);
      } else {
        this.$refs.ActivityReportModal.openArticle(payload);
      }
    },
    handleEventClick(info) {
      if (info.event.extendedProps && info.event.extendedProps.isLeaveEvent) {
        this.openLeavePopover(info);
        return;
      }
      // Picked from a day's "+N more" list: close that list first, or it stays
      // open on top of the activity window.
      this.closeMorePopover();
      const event = this.rawEvents.find((item) => String(item.id) === info.event.id);
      if (event) {
        this.$refs.ViewEventModal.open({ ...event });
      }
    },
    closeMorePopover() {
      const close = document.querySelector("#calendar-of-activities .fc-more-popover .fc-popover-close");
      if (close) close.click();
    },
    openLeavePopover(info) {
      const shell = this.$refs.calendarShell;
      const details = info.event.extendedProps.leaveDetails;
      if (!shell || !info.el || !details) return;

      const shellRect = shell.getBoundingClientRect();
      const blockRect = info.el.getBoundingClientRect();
      const width = 260;
      const estimatedHeight = 130;
      const left = Math.max(8, Math.min(blockRect.left - shellRect.left, shellRect.width - width - 8));
      let top = blockRect.bottom - shellRect.top + 6;
      if (top + estimatedHeight > shellRect.height) {
        top = Math.max(8, blockRect.top - shellRect.top - estimatedHeight - 6);
      }

      this.leavePopoverTrigger = info.el;
      this.leavePopover = { ...details, top, left };

      // Opened from the keyboard (Enter/Space on the block): move focus in.
      if (info.jsEvent && info.jsEvent.type === "keydown") {
        this.$nextTick(() => {
          if (this.$refs.leavePopoverClose) this.$refs.leavePopoverClose.focus();
        });
      }
    },
    closeLeavePopover(restoreFocus) {
      if (!this.leavePopover) return;
      this.leavePopover = null;
      const trigger = this.leavePopoverTrigger;
      this.leavePopoverTrigger = null;
      if (restoreFocus && trigger && document.body.contains(trigger)) trigger.focus();
    },
    handleDocumentMouseDown(event) {
      if (!this.leavePopover) return;
      const popover = this.$refs.leavePopover;
      if (popover && popover.contains(event.target)) return;
      if (this.leavePopoverTrigger && this.leavePopoverTrigger.contains(event.target)) return;
      this.closeLeavePopover(false);
    },
    handleDocumentKeydown(event) {
      if (event.key === "Escape" && this.leavePopover) this.closeLeavePopover(true);
    },
    handleWindowResize() {
      this.closeLeavePopover(false);
    },
    // Opens an activity from a shared link (/calendar?event=ID).
    openEventFromLink(id) {
      axios
        .get(`api/events/${id}`)
        .then((response) => {
          if (response.data) this.$refs.ViewEventModal.open(response.data);
        })
        .catch(() => {
          toastr.warning("This activity no longer exists, or you don't have access to it.", "Activity");
          this.clearEventQuery();
        });
    },
    // Drop ?event= once the activity is closed, so a reload doesn't reopen it.
    clearEventQuery() {
      if (!this.$route.query.event) return;
      const query = { ...this.$route.query };
      delete query.event;
      this.$router.replace({ query }).catch(() => {});
    },
    openEventById(id) {
      const event =
        this.rawEvents.find((item) => item.id === id) ||
        this.listEvents.find((item) => item.id === id);
      if (event) {
        this.$refs.ViewEventModal.open({ ...event });
        return;
      }

      axios
        .get(`api/events/${id}`)
        .then((response) => {
          if (response.data) this.$refs.ViewEventModal.open(response.data);
        })
        .catch(() => {
          toastr.error("Failed to load activity.", "Error");
        });
    },
    async exportActivitiesCsv() {
      if (this.exporting) return;
      const { start, end } = getPeriodRange(this.exportPeriod);
      this.exporting = true;
      let periodEvents;
      try {
        const response = await axios.get("api/calendar", {
          params: { from: formatLocalDate(start), to: formatLocalDate(end) },
        });
        periodEvents = this.applyTypeFilter(
          filterEventsByPeriod(response.data || [], this.exportPeriod)
        ).sort(this.compareByStartThenTitle);
      } catch (error) {
        toastr.error("Failed to export activities.", "Export");
        return;
      } finally {
        this.exporting = false;
      }

      if (!periodEvents.length) {
        toastr.warning("No activities found for the selected period.", "Export");
        return;
      }

      const rows = eventsToExportRows(periodEvents);
      const csv = Papa.unparse(rows);
      const labels = { today: "today", week: "this-week", month: "this-month" };
      const filename = `calendar-activities-${labels[this.exportPeriod] || "export"}.csv`;
      downloadCsv(csv, filename);
      toastr.success(`Exported ${periodEvents.length} activities.`, "Export");
    },
    getEvents(from, to) {
      const rangeFrom = from || (this.visibleRange && this.visibleRange.from);
      const rangeTo = to || (this.visibleRange && this.visibleRange.to);
      const requestToken = ++this.eventsRequestToken;

      this.eventsLoading = true;
      const params = {};
      if (rangeFrom && rangeTo) {
        params.from = rangeFrom;
        params.to = rangeTo;
      }

      return axios
        .get("api/calendar", { params })
        .then((response) => {
          if (requestToken !== this.eventsRequestToken) return;
          this.rawEvents = response.data || [];
          this.refreshCalendarEvents();
        })
        .catch(() => {
          if (requestToken !== this.eventsRequestToken) return;
          toastr.error("Failed to retrieve events.", "Error");
        })
        .finally(() => {
          if (requestToken !== this.eventsRequestToken) return;
          this.eventsLoading = false;
        });
    },
    getPendingReportsCount() {
      axios
        .get("api/calendar/pending-reports-count", {
          params: this.filterType ? { type: this.filterType } : {},
        })
        .then((response) => {
          this.pendingReportTotal = (response.data && response.data.count) || 0;
        })
        .catch(() => {});
    },
    getMyPendingReports() {
      this.pendingReportsLoading = true;
      axios
        .get("api/calendar/mine/pending-reports", { params: { limit: 10 } })
        .then((response) => {
          this.myPendingReports = (response.data && response.data.activities) || [];
        })
        .catch(() => {})
        .finally(() => {
          this.pendingReportsLoading = false;
        });
    },
    getMyStatusUpdates() {
      axios
        .get("api/calendar/mine/status-updates", { params: { limit: 5 } })
        .then((response) => {
          const data = response.data || {};
          this.myStatusUpdates = data.activities || [];
          this.myStatusUpdateTotal = data.count || 0;
          if (data.days) this.statusUpdateDays = data.days;
        })
        .catch(() => {});
    },
    goToUpdateStatus(eventId) {
      this.$router.push({
        name: "MyActivities",
        query: { event: String(eventId), status: "1" },
      });
    },
    goToSubmitReport(eventId) {
      this.$router.push({
        name: "MyActivities",
        query: { event: String(eventId), report: "1" },
      });
    },
    setSummaryPeriod(period) {
      if (this.summaryPeriod === period) return;
      this.summaryPeriod = period;
      this.loadSummaryEvents();
    },
    loadSummaryEvents() {
      const { start, end } = getPeriodRange(this.summaryPeriod);
      const from = formatLocalDate(start);
      const to = formatLocalDate(end);
      const requestToken = ++this.summaryRequestToken;

      this.summaryLoading = true;
      return axios
        .get("api/calendar", { params: { from, to } })
        .then((response) => {
          if (requestToken !== this.summaryRequestToken) return;
          const events = (response.data || []).filter(
            (event) => (event.status || "scheduled") !== "cancelled"
          );
          this.summaryEvents = filterEventsByPeriod(events, this.summaryPeriod).sort(
            this.compareByStartThenTitle
          );
        })
        .catch(() => {
          if (requestToken !== this.summaryRequestToken) return;
          toastr.error("Failed to load activity summary.", "Error");
        })
        .finally(() => {
          if (requestToken !== this.summaryRequestToken) return;
          this.summaryLoading = false;
        });
    },
    loadListEvents() {
      const { start, end } = getPeriodRange(this.listPeriod, this.listAnchor);
      const from = formatLocalDate(start);
      const to = formatLocalDate(end);
      const requestToken = ++this.listRequestToken;

      this.listLoading = true;
      return axios
        .get("api/calendar", { params: { from, to } })
        .then((response) => {
          if (requestToken !== this.listRequestToken) return;
          const events = response.data || [];
          this.listEvents = filterEventsByPeriod(events, this.listPeriod, this.listAnchor).sort(
            this.compareByStartThenTitle
          );
        })
        .catch(() => {
          if (requestToken !== this.listRequestToken) return;
          toastr.error("Failed to load activities.", "Error");
        })
        .finally(() => {
          if (requestToken !== this.listRequestToken) return;
          this.listLoading = false;
        });
    },
    async copySummaryActivities() {
      const events = this.filteredSummaryEvents;
      if (!events.length) return;

      const text = eventsToClipboardText(events);
      try {
        await copyTextToClipboard(text);
        toastr.success(
          `Copied ${events.length} ${events.length === 1 ? "activity" : "activities"} to clipboard.`,
          "Copied"
        );
      } catch (error) {
        toastr.error("Could not copy to clipboard.", "Error");
      }
    },
  },
  watch: {
    // A shared link opened while already on the calendar.
    "$route.query.event"(id) {
      if (id) this.openEventFromLink(id);
    },
  },
  created() {
    this.leavePopoverTrigger = null;
  },
  mounted() {
    const savedView = this.readPreference("calendar-view-mode");
    if (savedView === "calendar" || savedView === "list") {
      this.viewMode = savedView;
    } else if (this.isSmallScreen()) {
      // The month grid is cramped on phones, so start there on the list.
      this.viewMode = "list";
    }
    const savedLegend = this.readPreference("calendar-legend-open");
    this.legendOpen = savedLegend === null ? !this.isSmallScreen() : savedLegend === "1";
    this.showLeaveLayer = this.readPreference("calendar-show-leave") !== "0";

    document.addEventListener("mousedown", this.handleDocumentMouseDown);
    document.addEventListener("keydown", this.handleDocumentKeydown);
    window.addEventListener("resize", this.handleWindowResize);

    this.$nextTick(() => this.initCalendar());
    this.loadSummaryEvents();
    this.getPendingReportsCount();
    this.getMyPendingReports();
    this.getMyStatusUpdates();
    if (this.$route.query.event) this.openEventFromLink(this.$route.query.event);
    if (this.viewMode === "list") {
      this.loadListEvents();
    }
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleDocumentMouseDown);
    document.removeEventListener("keydown", this.handleDocumentKeydown);
    window.removeEventListener("resize", this.handleWindowResize);
    if (this.fetchEventsTimer) {
      clearTimeout(this.fetchEventsTimer);
    }
    if (this.calendar) {
      this.calendar.destroy();
    }
  },
};
</script>

<style scoped>
/* ── Page shell ── */
.calendar-page {
  background: linear-gradient(180deg, rgba(77, 141, 255, 0.03) 0%, transparent 240px);
}

/* ── Unified toolbar card ── */
.calendar-toolbar-card {
  grid-column: 1 / -1;
  border-radius: 14px !important;
}

.toolbar-card-body {
  padding: 0.75rem 1rem !important;
}

.toolbar-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem 1.25rem;
}

.toolbar-brand {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 0 1 auto;
}

.toolbar-title {
  font-size: 1.15rem;
  font-weight: 800;
  color: #1e2a3b;
}

.head-icon {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background: linear-gradient(135deg, rgba(77, 141, 255, 0.14), rgba(77, 141, 255, 0.05));
  color: #4d8dff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.page-subtitle {
  color: #768192;
  font-size: 0.8rem;
  margin-top: 0.1rem;
}

.toolbar-stats {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  flex: 1 1 auto;
}

.mini-stat {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  background: rgba(77, 141, 255, 0.07);
  border: 1px solid rgba(77, 141, 255, 0.12);
  font-size: 0.72rem;
  color: #5a6a7e;
  white-space: nowrap;
}

.mini-stat strong {
  color: #4d8dff;
  font-weight: 800;
}

.mini-stat--week {
  background: rgba(43, 182, 163, 0.07);
  border-color: rgba(43, 182, 163, 0.14);
}

.mini-stat--week strong {
  color: #2bb6a3;
}

.mini-stat--danger {
  background: rgba(229, 83, 83, 0.09);
  border-color: rgba(229, 83, 83, 0.22);
  color: #c92a2a;
}

.mini-stat--danger strong {
  color: #e55353;
}

.mini-stat--posting {
  background: rgba(24, 119, 242, 0.09);
  border-color: rgba(24, 119, 242, 0.2);
  color: #1877f2;
}

.mini-stat--posting strong {
  color: #1877f2;
}

.toolbar-top-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
  margin-left: auto;
  flex-shrink: 0;
}

.view-toggle {
  display: inline-flex;
  padding: 0.15rem;
  border-radius: 10px;
  background: rgba(0, 0, 21, 0.05);
  border: 1px solid rgba(0, 0, 21, 0.06);
}

.view-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: none;
  background: transparent;
  color: #768192;
  font-size: 0.76rem;
  font-weight: 700;
  padding: 0.35rem 0.65rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.view-toggle-btn:hover {
  color: #4d8dff;
}

.view-toggle-btn.active {
  background: #fff;
  color: #4d8dff;
  box-shadow: 0 1px 3px rgba(43, 58, 85, 0.1);
}

/* ── Controls: filters on the left, legend + export on the right ── */
.toolbar-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem 1rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 21, 0.06);
}

.toolbar-controls-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem 1rem;
}

.toolbar-control {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
}

.toolbar-control-label {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 700;
  color: #768192;
  white-space: nowrap;
}

.toolbar-control .form-control {
  width: auto;
  min-width: 140px;
  border-radius: 9px;
  border-color: rgba(0, 0, 21, 0.1);
}

.toolbar-control .view-toggle-btn {
  padding-top: 0.3rem;
  padding-bottom: 0.3rem;
}

.export-btn {
  width: 31px;
  height: 31px;
  padding: 0;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.legend-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 31px;
  padding: 0 0.7rem;
  border: 1px solid rgba(0, 0, 21, 0.1);
  border-radius: 9px;
  background: #fff;
  color: #5a6a7e;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
}

.legend-toggle:hover,
.legend-toggle.open {
  color: #4d8dff;
  border-color: rgba(77, 141, 255, 0.35);
}

.legend-toggle-caret {
  font-size: 0.6rem;
}

.summary-period-toggle {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.25rem;
  padding: 0.2rem;
  border-radius: 10px;
  background: rgba(0, 0, 21, 0.04);
}

.summary-period-btn {
  border: none;
  background: transparent;
  color: #768192;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.35rem 0.5rem;
  border-radius: 8px;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;
  white-space: nowrap;
}

.summary-period-btn:hover {
  color: #4d8dff;
}

.summary-period-btn.active {
  background: #fff;
  color: #4d8dff;
  box-shadow: 0 1px 3px rgba(43, 58, 85, 0.1);
}

/* ── Leave & pass slips switch ── */
.layer-switch {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 31px;
  padding: 0 0.65rem 0 0.5rem;
  border: 1px solid rgba(0, 0, 21, 0.1);
  border-radius: 9px;
  background: #fff;
  color: #768192;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
}

.layer-switch.on {
  color: #2b3a55;
}

.layer-switch-track {
  position: relative;
  width: 26px;
  height: 15px;
  border-radius: 999px;
  background: #c4cad4;
  transition: background 0.15s ease;
}

.layer-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s ease;
}

.layer-switch.on .layer-switch-track {
  background: #4d8dff;
}

.layer-switch.on .layer-switch-thumb {
  transform: translateX(11px);
}

/* ── Legend: one labelled row per group ── */
.legend-grid {
  display: grid;
  grid-template-columns: max-content 1fr;
  align-items: baseline; /* labels line up with the first line of a wrapped row */
  gap: 0.45rem 1rem;
  margin-top: 0.7rem;
  padding-top: 0.7rem;
  border-top: 1px solid rgba(0, 0, 21, 0.06);
}

.legend-row-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.35px;
  text-transform: uppercase;
  color: #9aa5b5;
}

.legend-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.9rem;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  color: #5a6a7e;
}

/* Legend samples are miniature calendar blocks; their status/marker styling
   comes from the shared "Event markers" rules below. */
.legend-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
  overflow: hidden;
}

.legend-chip ::v-deep .fc-evt-body {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  width: 100%;
  height: 100%;
  padding: 0 4px;
  border-radius: inherit;
}

@media (max-width: 1099.98px) {
  /* Stats drop under the title so the buttons keep the first line. */
  .toolbar-stats {
    order: 3;
    flex-basis: 100%;
  }
}

@media (max-width: 767.98px) {
  .toolbar-top-actions {
    width: 100%;
    margin-left: 0;
    justify-content: space-between;
  }

  .toolbar-controls-group {
    width: 100%;
  }

  .toolbar-control {
    flex: 1 1 auto;
  }

  .toolbar-control .form-control {
    flex: 1;
    min-width: 0;
  }

  .view-toggle-btn span {
    display: none;
  }
}

@media (min-width: 576px) {
  .view-toggle-btn span {
    display: inline;
  }
}

/* ── Two-column layout ── */
.calendar-layout {
  display: grid;
  grid-template-columns: 1fr 320px;
  grid-template-rows: auto 1fr;
  gap: 0.75rem;
  align-items: stretch;
}

.calendar-layout.layout-list {
  grid-template-columns: 1fr;
}

.calendar-layout.layout-list .calendar-main {
  grid-column: 1 / -1;
}

.calendar-main {
  grid-column: 1;
  min-width: 0;
}

.week-sidebar {
  grid-column: 2;
  min-width: 0;
  position: sticky;
  top: 1rem;
  align-self: start;
  max-height: calc(100vh - 2rem);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

@media (max-width: 1199px) {
  .calendar-layout {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
  }

  .calendar-toolbar-card,
  .calendar-main,
  .week-sidebar {
    grid-column: 1;
  }

  .week-sidebar {
    position: static;
    max-height: none;
  }
}

.calendar-toolbar-card,
.calendar-main,
.week-sidebar {
  border-radius: 14px !important;
}

.calendar-main-body {
  padding: 0.85rem 1rem !important;
}

.week-sidebar .card-body {
  padding: 0.85rem 1rem;
  overflow-y: auto;
  flex: 1;
}

/* ── Calendar shell ── */
.calendar-main-body--grid {
  padding: 1rem 1.25rem 1.25rem !important;
}

.calendar-shell {
  position: relative;
  background: #fff;
  padding: 0.25rem 0.15rem 0.15rem;
}

.calendar-fetch-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 5rem;
  background: rgba(255, 255, 255, 0.55);
  pointer-events: none;
}

/* ── Leave / pass slip popup ── */
.leave-popover {
  position: absolute;
  z-index: 1036; /* above the "+more" day list (1035), below Bootstrap modals (1040+) */
  width: 260px;
  max-width: calc(100% - 16px);
  padding: 0.7rem 0.85rem 0.8rem;
  background: #fff;
  border: 1px solid #e8ecf1;
  border-left: 3px solid var(--evt-accent, #768192);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(43, 58, 85, 0.14);
}

.leave-popover-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.45rem;
}

.leave-popover-kind {
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
}

.leave-popover-close {
  border: none;
  background: none;
  padding: 0 0.2rem;
  border-radius: 6px;
  color: #768192;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
}

.leave-popover-close:hover {
  background: #f1f3f6;
  color: #2b3a55;
}

.leave-popover-name {
  margin: 0 0 0.35rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: #2b3a55;
}

.leave-popover-line {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.15rem 0 0;
  font-size: 0.78rem;
  color: #5a6a7e;
}

/* ── List view ── */
.list-shell {
  border-radius: 16px;
  overflow: hidden;
}

.list-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  margin-bottom: 0.65rem;
}

.list-search-wrap {
  position: relative;
  flex: 1;
  min-width: 180px;
  max-width: 360px;
}

.list-search-icon {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9aa5b5;
  pointer-events: none;
  font-size: 0.85rem;
}

.list-search-input {
  padding-left: 2rem;
  border-radius: 9px;
  border-color: rgba(0, 0, 21, 0.1);
  background: #f8f9fb;
}

.list-search-input:focus {
  background: #fff;
  border-color: rgba(77, 141, 255, 0.35);
  box-shadow: 0 0 0 3px rgba(77, 141, 255, 0.1);
}

.list-period-nav {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.list-nav-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 0, 21, 0.1);
  border-radius: 8px;
  background: #fff;
  color: #4a5568;
  cursor: pointer;
}

.list-nav-btn:hover {
  background: #f5f8fc;
  border-color: #c5d4e8;
  color: #2b3a55;
}

.list-period-label {
  min-width: 9.5rem;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 700;
  color: #2b3a55;
}

.list-current-btn {
  border: none;
  border-radius: 8px;
  padding: 0.3rem 0.6rem;
  background: rgba(77, 141, 255, 0.08);
  color: #4d8dff;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
}

.list-current-btn:hover {
  background: rgba(77, 141, 255, 0.14);
}

.list-count {
  font-size: 0.8rem;
  font-weight: 700;
  color: #768192;
  white-space: nowrap;
}

.list-empty {
  text-align: center;
  padding: 3.5rem 1.5rem;
  color: #adb5c4;
}

.list-empty i {
  font-size: 2.25rem;
  display: block;
  margin-bottom: 0.65rem;
  opacity: 0.45;
}

.list-empty p {
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
  color: #768192;
}

.calendar-activity-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.cal-list-row {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 0.95rem 1rem;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 21, 0.07);
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.cal-list-row:hover {
  border-color: rgba(77, 141, 255, 0.28);
  box-shadow: 0 4px 14px rgba(77, 141, 255, 0.08);
  transform: translateY(-1px);
}

.cal-list-row--report {
  border-color: rgba(229, 83, 83, 0.35);
  background: linear-gradient(90deg, rgba(229, 83, 83, 0.05) 0%, #fff 18%);
}

.cal-list-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 52px;
  padding: 0.45rem 0.55rem;
  border-radius: 12px;
  background: rgba(77, 141, 255, 0.08);
  flex-shrink: 0;
}

.cal-list-row--report .cal-list-date {
  background: rgba(229, 83, 83, 0.12);
}

.cal-list-day {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #4d8dff;
}

.cal-list-row--report .cal-list-day {
  color: #e55353;
}

.cal-list-num {
  font-size: 0.85rem;
  font-weight: 800;
  color: #2b3a55;
  line-height: 1.2;
}

.cal-list-body {
  flex: 1;
  min-width: 0;
}

.cal-list-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.6rem;
  margin-bottom: 0.2rem;
}

.cal-list-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #2b3a55;
  line-height: 1.35;
}

.cal-list-purpose {
  margin: 0 0 0.35rem;
  font-size: 0.82rem;
  color: #768192;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cal-list-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.75rem;
}

.cal-list-meta-item {
  font-size: 0.76rem;
  color: #768192;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.cal-list-aside {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex-shrink: 0;
  padding-top: 0.1rem;
}

.cal-list-type,
.cal-list-status {
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
}

.cal-list-type {
  color: #fff;
}

/* Soft pill; colors come from getStatusPillStyle() */
.cal-list-status {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border: 1px solid transparent;
}

.cal-list-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
}

.cal-list-chip--danger {
  background: #e55353;
  color: #fff;
}

.cal-list-chip--posting {
  background: #1877f2;
  color: #fff;
}

.cal-list-chip--private {
  background: rgba(108, 117, 125, 0.16);
  color: #495057;
  border: 1px solid rgba(108, 117, 125, 0.35);
}

.cal-list-chevron {
  color: #c4cad4;
  font-size: 0.85rem;
  margin-top: 0.15rem;
  transition: color 0.15s ease, transform 0.15s ease;
}

.cal-list-row:hover .cal-list-chevron {
  color: #4d8dff;
  transform: translateX(2px);
}

@media (max-width: 767.98px) {
  .cal-list-row {
    flex-wrap: wrap;
  }

  .cal-list-aside {
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding-top: 0.35rem;
    border-top: 1px solid rgba(0, 0, 21, 0.05);
  }
}

/* ── Week sidebar ── */
.sidebar-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid rgba(0, 0, 21, 0.06);
}

.sidebar-header-actions {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;
}

.sidebar-copy-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #768192;
  border-color: rgba(0, 0, 21, 0.1);
}

.sidebar-copy-btn:hover:not(:disabled) {
  color: #4d8dff;
  border-color: rgba(77, 141, 255, 0.35);
  background: rgba(77, 141, 255, 0.06);
}

.summary-period-toggle {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.35rem;
  margin-bottom: 1rem;
  padding: 0.25rem;
  border-radius: 12px;
  background: rgba(0, 0, 21, 0.04);
}

.summary-period-btn {
  border: none;
  background: transparent;
  color: #768192;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.45rem 0.35rem;
  border-radius: 9px;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;
}

.summary-period-btn:hover {
  color: #4d8dff;
}

.summary-period-btn.active {
  background: #fff;
  color: #4d8dff;
  box-shadow: 0 1px 4px rgba(43, 58, 85, 0.1);
}

.sidebar-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2b3a55;
}

.sidebar-subtitle {
  color: #768192;
  font-size: 0.78rem;
}

.sidebar-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 0.5rem;
  border-radius: 999px;
  background: rgba(43, 182, 163, 0.12);
  color: #2bb6a3;
  font-size: 0.8rem;
  font-weight: 700;
}

.sidebar-count-info {
  background: rgba(77, 141, 255, 0.12);
  color: #3a6abf;
}

.status-updates-block {
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(77, 141, 255, 0.15);
}

.status-updates-title {
  color: #3a6abf;
}

.status-updates-more {
  display: inline-block;
  margin-top: 0.55rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4d8dff;
}

.sidebar-count-danger {
  background: rgba(229, 83, 83, 0.15);
  color: #e55353;
}

.pending-reports-block {
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(229, 83, 83, 0.15);
}

.pending-reports-header {
  border-bottom-color: rgba(229, 83, 83, 0.12);
}

.week-date-block-danger {
  background: rgba(229, 83, 83, 0.12);
}

.week-date-block-danger .week-day {
  color: #e55353;
}

.sidebar-empty {
  text-align: center;
  padding: 2.5rem 1rem;
  color: #adb5c4;
}

.sidebar-empty i {
  font-size: 2rem;
  display: block;
  margin-bottom: 0.5rem;
  opacity: 0.5;
}

.sidebar-empty p {
  margin: 0;
  font-size: 0.85rem;
}

.week-list {
  list-style: none;
  padding: 0;
}

.week-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem;
  margin-bottom: 0.5rem;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 21, 0.06);
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
}

.week-item:last-child {
  margin-bottom: 0;
}

.week-item:focus-visible {
  outline: 2px solid #4d8dff;
  outline-offset: 2px;
}

.week-item:hover {
  border-color: rgba(77, 141, 255, 0.25);
  background: rgba(77, 141, 255, 0.03);
  box-shadow: 0 2px 8px rgba(77, 141, 255, 0.08);
}

.week-item-needs-report {
  border-color: rgba(229, 83, 83, 0.45);
  background: rgba(229, 83, 83, 0.06);
  border-left: 4px solid #e55353;
}

.week-item-needs-report:hover {
  border-color: rgba(229, 83, 83, 0.55);
  background: rgba(229, 83, 83, 0.09);
  box-shadow: 0 2px 10px rgba(229, 83, 83, 0.15);
}

.week-item-needs-report .week-date-block {
  background: rgba(229, 83, 83, 0.12);
}

.week-item-needs-report .week-day {
  color: #e55353;
}

.week-date-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 44px;
  padding: 0.4rem 0.5rem;
  border-radius: 10px;
  background: rgba(77, 141, 255, 0.08);
  flex-shrink: 0;
}

.week-day {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #4d8dff;
}

.week-date {
  font-size: 0.8rem;
  font-weight: 800;
  color: #2b3a55;
  line-height: 1.2;
}

.week-content {
  flex: 1;
  min-width: 0;
}

.week-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #2b3a55;
  margin: 0 0 0.3rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.week-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.5rem;
}

.week-meta-item {
  font-size: 0.72rem;
  color: #768192;
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
}

.week-type-pill {
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
}

.week-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.35rem;
}

.week-badge {
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
}

.week-badge-warning {
  background: rgba(224, 144, 31, 0.15);
  color: #e0901f;
}

.week-badge-report {
  background: #e55353;
  color: #fff;
  font-size: 0.65rem;
  padding: 0.15rem 0.5rem;
  box-shadow: 0 1px 4px rgba(229, 83, 83, 0.35);
}

.week-badge-posting {
  background: #1877f2;
  color: #fff;
  font-size: 0.65rem;
  padding: 0.15rem 0.5rem;
  box-shadow: 0 1px 4px rgba(24, 119, 242, 0.35);
}

/* Soft pill; colors come from getStatusPillStyle() */
.week-badge-status {
  font-size: 0.65rem;
  padding: 0.15rem 0.5rem;
  border: 1px solid transparent;
}

.week-badge-update {
  background: rgba(77, 141, 255, 0.12);
  color: #3a6abf;
  font-size: 0.65rem;
  padding: 0.15rem 0.5rem;
  border: 1px solid rgba(77, 141, 255, 0.4);
}

.week-badge-private {
  background: rgba(108, 117, 125, 0.16);
  color: #495057;
  font-size: 0.65rem;
  padding: 0.15rem 0.5rem;
  border: 1px solid rgba(108, 117, 125, 0.35);
}

.week-chevron {
  color: #c4cad4;
  font-size: 0.75rem;
  margin-top: 0.35rem;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}

.week-item:hover .week-chevron {
  color: #4d8dff;
  transform: translateX(2px);
}

/* ── FullCalendar ── */
#calendar-of-activities ::v-deep .fc {
  font-family: inherit;
  --fc-border-color: #e8ecf1;
  --fc-today-bg-color: #f0f6ff;
  --fc-neutral-bg-color: #f8f9fb;
  --fc-page-bg-color: #fff;
  --fc-event-border-color: transparent;
  padding: 0.35rem 0.65rem 0.65rem;
}

#calendar-of-activities ::v-deep .fc-scrollgrid {
  border: none;
  border-radius: 10px;
  overflow: hidden;
}

#calendar-of-activities ::v-deep .fc-scrollgrid-sync-table {
  width: 100% !important;
}

#calendar-of-activities ::v-deep .fc-header-toolbar {
  padding: 0.25rem 0.35rem 0.85rem;
  margin-bottom: 0 !important;
}

#calendar-of-activities ::v-deep .fc-toolbar-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #2b3a55;
}

#calendar-of-activities ::v-deep .fc-button {
  background: #fff;
  border: 1px solid #dde3ea;
  color: #4a5568;
  border-radius: 8px;
  text-transform: capitalize;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0.35rem 0.7rem;
  box-shadow: none;
}

#calendar-of-activities ::v-deep .fc-button:hover,
#calendar-of-activities ::v-deep .fc-button:focus {
  background: #f5f8fc;
  border-color: #c5d4e8;
  color: #2b3a55;
  box-shadow: none;
}

#calendar-of-activities ::v-deep .fc-button-group {
  gap: 0.25rem;
}

#calendar-of-activities ::v-deep .fc-button-group > .fc-button {
  border-radius: 8px !important;
}

#calendar-of-activities ::v-deep .fc-col-header-cell {
  padding: 0.65rem 0.5rem;
  border-color: #e8ecf1 !important;
  background: #f8f9fb;
}

#calendar-of-activities ::v-deep .fc-col-header-cell-cushion {
  color: #768192;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-decoration: none;
}

#calendar-of-activities ::v-deep .fc-daygrid-day {
  border-color: #e8ecf1 !important;
}

#calendar-of-activities ::v-deep .fc-daygrid-day.fc-day-weekend {
  background: #fcfcfd;
}

#calendar-of-activities ::v-deep .fc-daygrid-day.fc-day-other {
  background: #fafbfc;
}

#calendar-of-activities ::v-deep .fc-daygrid-day.fc-day-other .fc-daygrid-day-number {
  color: #b0b8c4;
}

#calendar-of-activities ::v-deep .fc-daygrid-day.fc-day-today {
  background: #f0f6ff !important;
}

#calendar-of-activities ::v-deep .fc-daygrid-day.fc-day-has-pending-report {
  background: #fff5f5 !important;
}

#calendar-of-activities ::v-deep .fc-view-harness {
  padding: 0 0.35rem 0.35rem;
}

#calendar-of-activities ::v-deep .fc-daygrid-day-top {
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem 0.65rem 0.35rem;
}

#calendar-of-activities ::v-deep .fc-daygrid-day-number {
  color: #4a5568;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.2rem 0.45rem;
  text-decoration: none;
  line-height: 1.2;
  border-radius: 6px;
}

#calendar-of-activities ::v-deep .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
  background: #4d8dff;
  color: #fff;
  font-weight: 700;
}

#calendar-of-activities ::v-deep .fc-daygrid-day.fc-day-has-pending-report .fc-daygrid-day-number {
  color: #e55353;
  font-weight: 700;
}

#calendar-of-activities ::v-deep .fc-daygrid-day-frame {
  min-height: 108px;
  padding: 0 2px 6px;
}

#calendar-of-activities ::v-deep .fc-daygrid-day-events {
  margin: 0;
  padding: 0.15rem 8px 8px;
}

#calendar-of-activities ::v-deep .fc-daygrid-event-harness {
  margin-top: 5px;
}

#calendar-of-activities ::v-deep .fc-daygrid-event-harness:first-child {
  margin-top: 2px;
}

#calendar-of-activities ::v-deep .fc-daygrid-block-event {
  margin-top: 5px;
}

#calendar-of-activities ::v-deep .fc-h-event .fc-event-main {
  padding: 0;
}

#calendar-of-activities ::v-deep .fc-h-event .fc-evt-body {
  padding: 6px 12px;
  min-height: 28px;
}

#calendar-of-activities ::v-deep .fc-daygrid-event {
  margin: 0 1px;
}

#calendar-of-activities ::v-deep .fc-event {
  border: none !important;
  border-radius: 5px;
  padding: 0;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  box-shadow: none;
  margin: 0;
}

#calendar-of-activities ::v-deep .fc-evt-body {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  line-height: 1.35;
  min-height: 28px;
  overflow: hidden;
}

#calendar-of-activities ::v-deep .fc-evt-time {
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 700;
  opacity: 0.9;
}

#calendar-of-activities ::v-deep .fc-evt-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

#calendar-of-activities ::v-deep .fc-event:hover {
  filter: brightness(1.06);
  z-index: 2;
}

#calendar-of-activities ::v-deep .fc-daygrid-more-link {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  color: #4d8dff;
  padding: 5px 10px;
  margin: 6px 8px 2px;
  text-align: left;
}

#calendar-of-activities ::v-deep .fc-daygrid-more-link:hover {
  background: rgba(77, 141, 255, 0.08);
  border-radius: 4px;
  text-decoration: none;
  color: #3a7ae8;
}

/* ── Event markers ──
   Shared by calendar blocks and legend chips, so the legend always shows
   exactly what the calendar draws. */
#calendar-of-activities ::v-deep .fc-evt-flag,
.legend-chip ::v-deep .fc-evt-flag {
  flex-shrink: 0;
  font-size: 0.65rem;
  line-height: 1;
  opacity: 0.95;
}

#calendar-of-activities ::v-deep .fc-evt-flag--report,
.legend-chip ::v-deep .fc-evt-flag--report {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: #e55353;
  font-weight: 800;
  font-size: 0.6rem;
}

#calendar-of-activities ::v-deep .fc-evt-flag--post,
.legend-chip ::v-deep .fc-evt-flag--post {
  opacity: 1;
}

#calendar-of-activities ::v-deep .fc-evt-flag--mode,
.legend-chip ::v-deep .fc-evt-flag--mode {
  opacity: 0.85;
}

#calendar-of-activities ::v-deep .fc-event.fc-needs-report {
  font-weight: 600;
}

#calendar-of-activities ::v-deep .fc-event.fc-status-cancelled,
.legend-chip.fc-status-cancelled {
  opacity: 0.45;
}

#calendar-of-activities ::v-deep .fc-event.fc-status-cancelled .fc-evt-title {
  text-decoration: line-through;
}

#calendar-of-activities ::v-deep .fc-event.fc-status-postponed,
.legend-chip.fc-status-postponed {
  opacity: 0.7;
  outline: 1.5px dashed rgba(255, 255, 255, 0.9);
  outline-offset: -3px;
}

#calendar-of-activities ::v-deep .fc-event.fc-status-completed,
.legend-chip.fc-status-completed {
  opacity: 0.6;
}

#calendar-of-activities ::v-deep .fc-event.fc-tentative,
.legend-chip.fc-tentative {
  background-image: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 4px,
    rgba(255, 255, 255, 0.22) 4px,
    rgba(255, 255, 255, 0.22) 8px
  ) !important;
}

#calendar-of-activities ::v-deep .fc-event.fc-private,
.legend-chip.fc-private {
  box-shadow: inset 3px 0 0 rgba(33, 37, 41, 0.75);
}

#calendar-of-activities ::v-deep .fc-event.fc-ready-for-posting,
.legend-chip.fc-ready-for-posting {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
}

#calendar-of-activities ::v-deep .fc-event.fc-private.fc-ready-for-posting,
.legend-chip.fc-private.fc-ready-for-posting {
  box-shadow: inset 3px 0 0 rgba(33, 37, 41, 0.75), inset 0 0 0 1px rgba(255, 255, 255, 0.35);
}

/* Leave / pass slips: tinted chip outlined in its accent color */
#calendar-of-activities ::v-deep .fc-leave-pass-slip-event .fc-evt-body,
.legend-chip.fc-leave-pass-slip-event ::v-deep .fc-evt-body {
  box-shadow: inset 0 0 0 1px var(--evt-accent);
  border-radius: inherit;
}

#calendar-of-activities ::v-deep .fc-leave-pass-slip-event .fc-evt-title {
  font-weight: 600;
}

#calendar-of-activities ::v-deep .fc-daygrid-body {
  padding: 0;
}
</style>

<!-- Unscoped: FullCalendar creates the "+more" popover at runtime, so its
     elements never get this component's scoped data attribute -->
<style>
/* FullCalendar puts this at z-index 9999, above Bootstrap modals (1050). Keep it
   over the page but under the modal backdrop, so it can never cover a modal. */
#calendar-of-activities .fc-more-popover {
  z-index: 1035;
}

.fc-more-popover {
  border: 1px solid #e8ecf1 !important;
  border-radius: 10px !important;
  box-shadow: 0 8px 24px rgba(43, 58, 85, 0.12) !important;
  overflow: hidden;
  font-family: inherit;
}

.fc-more-popover .fc-popover-header {
  background: #f8f9fb;
  border-bottom: 1px solid #e8ecf1;
  padding: 0.75rem 1rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #2b3a55;
}

.fc-more-popover .fc-popover-body {
  padding: 0.5rem;
  max-height: 280px;
  overflow-y: auto;
  background: #fff;
}

.fc-more-popover .fc-daygrid-event-harness {
  margin-top: 5px;
}

.fc-more-popover .fc-event {
  border-radius: 5px;
  border: none !important;
  font-size: 0.75rem;
}

.fc-more-popover .fc-evt-body {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  line-height: 1.35;
  min-height: 28px;
}
</style>
